import React from 'react';
type Tome = {
  number: string;
  title: string;
  range: string;
  promise: string;
  badge: string;
  flag?: string;
  contents: string[];
};
const tomes: Tome[] = [{
  number: '01',
  title: 'Foundations & Safe Landings',
  range: '7 Ready-Run Sessions · 50 pages',
  promise: 'The floor of every class: landings, rolls, balance and ground movement — taught landing-first.',
  badge: 'bg-gold-deep',
  contents: ['The Landing-First Ladder: from the floor to a low drop, step by step', 'The safety roll in 4 touch-points — taught without bruises', 'The Balance Ladder + quadrupedal crawl circuits', 'The 3-Second Scan: risk-check any space before a drill', 'Opening games like Shark & Islands and Traffic Lights', 'Sessions 1–7, incl. a first-ever class for kids and one for teens/rec']
}, {
  number: '02',
  title: 'Vaults & Obstacle Skills',
  range: '11 Ready-Run Sessions · 50 pages',
  promise: 'Every core vault, from the first step-over to the kong — with a way in for students who can’t do it yet.',
  badge: 'bg-teal',
  contents: ['The Vault Progression Ladder: step → safety → lazy → kong → dash', 'The Box-to-Rail Bridge: moving a vault off the box safely', 'Kong regressions for “I can’t do the kong yet”', 'Vault games: Vault Relay, Add-a-Vault, Beat-the-Clock', 'Keeping a vault line moving — no bored kids at the box', 'Sessions 8–18, incl. a confidence day for a nervous group']
}, {
  number: '03',
  title: 'Jumps, Precision & Drops',
  range: '11 Ready-Run Sessions · 50 pages',
  promise: 'Distance, aim and height control — so every jump ends in a stuck landing.',
  badge: 'bg-gold-deep',
  contents: ['The Jump Engine: load, swing, extend, absorb', 'The Precision Dial: shrink the target, grow the gap', 'The Drop Ladder: controlling height step by step', 'Go / no-go gap judgement students can learn', 'Games: Precision Target, Jump Course Relay', 'Sessions 19–29, incl. a small-space jump class']
}, {
  number: '04',
  title: 'Walls, Climbing & Bars',
  range: '11 Ready-Run Sessions · 50 pages',
  promise: 'Getting vertical safely: walls, cat leaps, climb-ups, hanging and swinging.',
  badge: 'bg-teal',
  contents: ['The Wall Ladder: wall pop → tic-tac → wall run → cat leap → climb-up', 'The Hang-to-Climb Bridge for bar work', 'Underbar, bar traverse and a low, matted swing-release build-up', 'Where to stand when you spot vertical skills', 'Wall & bar stations with the gym gear you already have', 'Sessions 30–40, incl. a grip & strength day']
}, {
  number: '05',
  title: 'Flow, Lines & Creativity',
  range: '11 Ready-Run Sessions · 50 pages',
  promise: 'Chain it all together: lines, flow and creative games that work in any space.',
  badge: 'bg-gold-deep',
  contents: ['The Line Builder: connect two moves, add a third, smooth it out', 'The Flow Loop: circuits that never stop moving', 'Challenge cards, Parkour Freeze Flow, Lava Lines', 'How to run an open jam and a safe showcase day', 'Flow courses for no-gear and low-gear spaces', 'Sessions 41–51, incl. an end-of-term showcase class']
}, {
  number: '06',
  title: 'The Coach’s Control Room',
  range: '8 Ready-Run Sessions + master planners',
  promise: 'The other volumes tell you what to teach. This one shows you how to run the room — and the whole program.',
  badge: 'bg-teal',
  flag: 'Runs the program',
  contents: ['The Full-Gym Rotation: stations that keep every student moving', 'The Mixed-Level Grid: one drill, three levels, at once', 'The Readiness Check: when a student moves up', '12-week term and 36-week year planners', 'Formats: after-school, holiday camp, PE, trial class, family sessions', 'Pre-class risk sweep + incident log (initials only)']
}];
export function Tomes() {
  return <section aria-labelledby="tomes-heading" className="w-full bg-navy" data-id="element-229" data-mp="378sr84k5p6eg">
      <div className="max-w-6xl mx-auto px-5 py-16 sm:py-20" data-id="element-230" data-mp="37alxvjurrqmg">
        <div className="text-center" data-id="element-231" data-mp="3lxxdpxxzhwsd">
          <p className="text-[11px] font-bold uppercase tracking-[1.6px] text-gold-deep" data-id="element-232" data-mp="3lz03g2jklt6p">
            The complete curriculum
          </p>
          <h2 id="tomes-heading" className="mt-3 font-display font-bold uppercase text-ink text-3xl sm:text-5xl" data-id="element-233" data-mp="3uxu4q67uj7ql">
            Inside the megapack — six volumes
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base text-ink-soft" data-id="element-234" data-mp="8rf2zw0l9a8h">
            A real skill progression — each volume builds on the one before —
            plus the control room that turns 59 sessions into a full program.
          </p>
        </div>

        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-235" data-mp="2akmip6k45tuw">
          {tomes.map(tome => <article key={tome.number} className={`flex flex-col rounded-2xl border bg-navy-card p-6 shadow-card ${tome.flag ? 'border-gold ring-1 ring-gold' : 'border-navy-line'}`} data-id="element-236" data-mp="25lrvztj0zl5i">
              <div className="flex items-center gap-2.5" data-id="element-237" data-mp="bas7zowhjfhg">
                <span className={`flex h-8 w-8 items-center justify-center rounded-lg font-display text-lg text-white ${tome.badge}`} data-id="element-238" data-mp="gaem00k50zrb">
                  {tome.number}
                </span>
                <span className="text-[11px] font-bold uppercase tracking-widest text-ink-muted" data-id="element-239" data-mp="6djmbec4otbg">
                  Volume {tome.number}
                </span>
                {tome.flag && <span className="ml-auto rounded bg-gold px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-gold-ink" data-id="element-240" data-mp="3tgeqcd15prpk">
                    {tome.flag}
                  </span>}
              </div>
              <h3 className="mt-3 text-lg font-bold leading-snug text-ink" data-id="element-241" data-mp="1rpbczfzvtz1b">
                {tome.title}
              </h3>
              <p className="mt-1 text-[11px] font-bold uppercase tracking-widest text-gold-deep" data-id="element-242" data-mp="1q6l5qi1acvny">
                {tome.range}
              </p>
              <p className="mt-2 text-sm text-ink-soft" data-id="element-243" data-mp="1g9otq2c6hren">{tome.promise}</p>
              <ul className="mt-4 grid gap-2 border-t border-navy-line pt-4" data-id="element-244" data-mp="1bbn9he30nm1l">
                {tome.contents.map(item => <li key={item} className="flex gap-2 text-[13px] text-ink" data-id="element-245" data-mp="28zfbqmgxldg1">
                    <span className="mt-[7px] h-1.5 w-1.5 flex-none rounded-full bg-gold" data-id="element-246" data-mp="240cald42nrdc" />
                    {item}
                  </li>)}
              </ul>
            </article>)}
        </div>
      </div>
    </section>;
}